import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { PostOrderRequest } from 'app/core/requests/PostOrderRequest';
import { AuthenticationService } from 'app/core/service';
import { PaymentService } from 'app/core/service/payment.service';
import { ToastrService } from 'ngx-toastr';
import {Subject} from 'rxjs';
import {FlexibleBenefit} from "../../../../core/models/flexible-benefit";
import {InsuranceService} from "../../../../core/service/insurance.service";
import {takeUntil} from "rxjs/operators";
import {BrowserDetectionService} from "../../../../core/service/browserdetection.service";
import {Browser} from "@capacitor/browser";
import {ConsentService} from "../../../../core/service/consent.service";
import {Router} from "@angular/router";
import {isMobile, getInsuranceReturnUrl} from "../../../../core/helpers/constants";

@Component({
  selector: 'app-benefit-confirm-modal',
  templateUrl: './benefit-confirm-modal.component.html',
  styleUrls: ['./benefit-confirm-modal.component.scss'],
  providers: [TranslatePipe]
})
export class BenefitConfirmModalComponent implements OnInit, OnDestroy {

  @Input() flexibleBenefit: FlexibleBenefit;

  public termsAccepted = false;
  public isLoading = false;
  public privacyPolicyUrl = '';

  private ngUnsubscribe = new Subject<void>();
  private subdomain: string;

  constructor(
    public activeModal: NgbActiveModal,
    private paymentService: PaymentService,
    private authenticationService: AuthenticationService,
    private toastr: ToastrService,
    private translate: TranslatePipe,
    private insuranceService: InsuranceService,
    private browserDetectionService: BrowserDetectionService,
    private consentService: ConsentService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.subdomain = this.router.url.split('/').filter(s => s.length > 0)[0];
    this.privacyPolicyUrl = "teams.public.onecdn.static.microsoft/evergreen-assets/safelinks/2/atp-safelinks.html";
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public confirm(): void {
    if (!this.termsAccepted || this.isLoading) {
      return;
    }

    this.isLoading = true;
    const userId = this.authenticationService.currentUserValue.userId;

    // Check if the user already has an insurance order — if yes skip placing a new one
    this.insuranceService.getInsuranceOrders(userId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (orders) => {
          if (orders && orders.length > 0) {
            // Already has an order — just redirect to the platform
            this.onOrderSuccess();
          } else {
            this.placeOrder();
          }
        },
        error: (error: any) => this.onFail(error)
      });
  }

  private placeOrder(): void {
    const currentUser = this.authenticationService.currentUserValue;

    const params: PostOrderRequest = {
      benefitId: this.flexibleBenefit.id,
      billingAddressId: 0,
      newCard: false,
      cardInfoId: 0,
      noOfUnits: 1,
      saveCard: false,
      shippingAddressId: 0,
      userId: currentUser.userId,
      beneficiaryId: null,
      phoneNumber: currentUser.phoneNumber,
      isAutoOrderEnabled: false,
      isAutoOrderDynamicQuantity: false,
      meta: null,
      dynamicAttributes: [
        { propertyName: 'InsuranceTermsAndConditionsAccepted', propertyValue: true, propertyDisplay: true },
      ],
      payWithCardOnly: true,
      payFromSalary: false
    };

    this.paymentService
      .postSimpleOrder(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => this.onOrderSuccess(),
        error: (error: any) => this.onFail(error),
      });
  }

  private onOrderSuccess(): void {
    const returnUrl = getInsuranceReturnUrl(this.flexibleBenefit?.name) ?? undefined;
    this.insuranceService
      .getAuthToken(returnUrl)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ({ url }) => {
          this.isLoading = false;
          this.performRedirect(url);
          this.activeModal.close('ordered');
          this.router.navigate(['/' + this.subdomain + '/benefits-history/insurance']);
        },
        error: (error: any) => this.onFail(error),
      });
  }

  private performRedirect(url: string): void {
    if (this.browserDetectionService.isMobileOs() || isMobile()) {
      Browser.open({
        url: url,
        presentationStyle: 'popover'
      });
    } else {
      window.open(url, '_blank');
    }
  }

  private onFail(error: any): void {
    this.isLoading = false;

    const messages: string[] = error?.messages ?? ['ERRORS.GENERIC'];
    messages.forEach((msg) => {
      this.toastr.error(
        this.translate.transform(msg),
        this.translate.transform('ERROR'),
        { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 },
      );
    });
  }
}
